import { RecoveryError, RecoveryOutcome } from './errors';
import { VerificationStatus, isVerificationMessage } from './protocol';

declare global {
  interface Window {
    fastlyActionRecovery?: RecoveryController;
  }
}

export interface RecoverySnapshot {
  attemptId: string | null;
  status: VerificationStatus | 'waiting';
  outcome: RecoveryOutcome | null;
}
interface Waiter {
  resolve: (permit: Permit) => void;
  reject: (error: RecoveryError) => void;
  cleanup: () => void;
}
export interface Permit {
  generation: number;
  deadline: number;
}
interface Episode {
  id: string;
  started: number;
  deadline: number;
  ready: boolean;
  frame: Window | null;
  waiters: Set<Waiter>;
  readinessTimer: ReturnType<typeof setTimeout>;
  timer: ReturnType<typeof setTimeout>;
}
export interface RecoveryDiagnostic {
  outcome: RecoveryOutcome | 'complete';
  elapsedMs: number;
  retryCount: number;
}

/** No application state, action payloads, or network requests are owned by the episode. */
export class RecoveryController {
  private episode: Episode | null = null;
  private generation = 0;
  private listeners = new Set<() => void>();
  private snapshot: RecoverySnapshot = { attemptId: null, status: 'waiting', outcome: null };
  private retryCount = 0;
  private completedPermit: Permit | null = null;
  private lastStarted = 0;

  constructor(
    private readonly options: {
      origin: string;
      attemptId: () => string;
      now?: () => number;
      readinessMs?: number;
      verificationMs?: number;
      report?: (diagnostic: RecoveryDiagnostic) => void;
    }
  ) {}

  getSnapshot = (): RecoverySnapshot => this.snapshot;
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return (): void => {
      this.listeners.delete(listener);
    };
  };
  private publish(snapshot: RecoverySnapshot): void {
    this.snapshot = snapshot;
    this.listeners.forEach((listener) => listener());
  }
  private now(): number {
    return this.options.now?.() ?? Date.now();
  }
  get waiting(): boolean {
    return this.episode !== null;
  }
  get ownership(): number {
    return this.generation;
  }

  get completion(): Permit | null {
    return this.completedPermit;
  }

  assertActive(generation: number, signal?: AbortSignal, deadline = Infinity): void {
    if (generation !== this.generation || signal?.aborted) {
      throw new RecoveryError('cancelled');
    }
    if (this.now() >= deadline) {
      throw new RecoveryError('timed-out');
    }
    if (this.snapshot.outcome) {
      throw new RecoveryError(this.snapshot.outcome);
    }
  }

  wait(signal?: AbortSignal, callerDeadline = Infinity): Promise<Permit> {
    let result: Promise<Permit> | undefined;
    try {
      this.assertActive(this.generation, signal, callerDeadline);
    } catch (error) {
      result = Promise.reject(error);
    }
    if (!result) {
      if (!this.episode) {
        const started = this.now();
        this.lastStarted = started;
        this.retryCount = 0;
        this.episode = {
          id: this.options.attemptId(),
          started,
          deadline: started + (this.options.verificationMs ?? 60000),
          ready: false,
          frame: null,
          waiters: new Set(),
          readinessTimer: setTimeout(
            () => this.fail('timed-out'),
            this.options.readinessMs ?? 15000
          ),
          timer: setTimeout(() => this.fail('timed-out'), this.options.verificationMs ?? 60000)
        };
        this.publish({ attemptId: this.episode.id, status: 'waiting', outcome: null });
      }
      const episode = this.episode;
      result = new Promise((resolve, reject) => {
        const abort = (): void => {
          episode.waiters.delete(waiter);
          waiter.cleanup();
          reject(new RecoveryError('cancelled'));
          if (this.episode === episode && !episode.waiters.size) {
            this.finish('cancelled', false);
          }
        };
        const timer = Number.isFinite(callerDeadline)
          ? setTimeout(
              () => {
                episode.waiters.delete(waiter);
                waiter.cleanup();
                reject(new RecoveryError('timed-out'));
                if (this.episode === episode && !episode.waiters.size) {
                  this.finish('timed-out', false);
                }
              },
              Math.max(0, callerDeadline - this.now())
            )
          : undefined;
        const waiter: Waiter = {
          resolve: (permit): void => {
            if (this.now() >= callerDeadline) {
              reject(new RecoveryError('timed-out'));
            } else {
              resolve({ ...permit, deadline: Math.min(permit.deadline, callerDeadline) });
            }
          },
          reject,
          cleanup: (): void => {
            signal?.removeEventListener('abort', abort);
            clearTimeout(timer);
          }
        };
        episode.waiters.add(waiter);
        signal?.addEventListener('abort', abort, { once: true });
        if (signal?.aborted) {
          abort();
        }
      });
    }
    return result;
  }

  attachFrame(attemptId: string, frame: Window | null): void {
    if (this.episode?.id === attemptId) {
      this.episode.frame = frame;
    }
  }

  receive = (event: MessageEvent): void => {
    const episode = this.episode;
    if (
      episode &&
      episode.frame &&
      event.origin === this.options.origin &&
      event.source === episode.frame &&
      isVerificationMessage(event.data) &&
      event.data.attemptId === episode.id
    ) {
      if (
        this.now() >= episode.deadline ||
        (!episode.ready && this.now() >= episode.started + (this.options.readinessMs ?? 15000))
      ) {
        this.fail('timed-out');
      } else {
        const message = event.data;
        if (message.type === 'ready') {
          episode.ready = true;
          clearTimeout(episode.readinessTimer);
        } else if (message.type === 'cancel') {
          this.cancel();
        } else if (message.type === 'integration-error') {
          this.fail('integration-failed');
        } else if (message.type === 'status' && episode.ready) {
          if (message.status === 'error') {
            this.fail('challenge-failed');
          } else if (message.status === 'complete') {
            this.finish('complete');
          } else {
            this.publish({ ...this.snapshot, status: message.status });
          }
        }
      }
    }
  };

  private finish(outcome: RecoveryOutcome | 'complete', latch = true): void {
    const episode = this.episode;
    this.episode = null;
    if (episode) {
      if (outcome === 'complete') {
        this.completedPermit = { generation: this.generation, deadline: episode.deadline };
      }
      clearTimeout(episode.timer);
      clearTimeout(episode.readinessTimer);
      episode.frame = null;
      episode.waiters.forEach((waiter) => {
        waiter.cleanup();
        if (outcome === 'complete') {
          waiter.resolve({ generation: this.generation, deadline: episode.deadline });
        } else {
          waiter.reject(new RecoveryError(outcome));
        }
      });
      episode.waiters.clear();
      this.options.report?.({
        outcome,
        elapsedMs: this.now() - episode.started,
        retryCount: this.retryCount
      });
    }
    this.publish({
      attemptId: null,
      status: 'waiting',
      outcome: outcome === 'complete' || !latch ? null : outcome
    });
  }
  fail(outcome: RecoveryOutcome): void {
    if (
      !this.episode &&
      this.completedPermit &&
      (outcome === 'retry-rejected' || outcome === 'timed-out')
    ) {
      this.options.report?.({
        outcome,
        elapsedMs: this.now() - this.lastStarted,
        retryCount: this.retryCount
      });
    }
    this.finish(outcome);
  }
  cancel = (): void => {
    this.generation += 1;
    this.completedPermit = null;
    this.finish('cancelled');
  };
  navigate = (): void => {
    this.cancel();
    this.reset();
  };
  reset = (): void => {
    if (!this.episode) {
      this.generation += 1;
      this.completedPermit = null;
      this.publish({ attemptId: null, status: 'waiting', outcome: null });
    }
  };
  recordRetry(): void {
    this.retryCount += 1;
    this.options.report?.({
      outcome: 'complete',
      elapsedMs: this.now() - this.lastStarted,
      retryCount: this.retryCount
    });
  }
}
